<template>
  <v-container fluid>
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <div>
            <h1 class="text-h4 font-weight-bold">Board Committees</h1>
            <p class="text-body-2 text-medium-emphasis">Manage board committees and their members</p>
          </div>
          <v-btn
            color="primary"
            prepend-icon="mdi-plus"
            @click="openCreateDialog"
            size="large"
          >
            New Committee
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center">
              <v-avatar color="primary" class="mr-4">
                <v-icon>mdi-account-group</v-icon>
              </v-avatar>
              <div>
                <div class="text-h4 font-weight-bold">{{ committees.length }}</div>
                <div class="text-caption text-medium-emphasis">Total Committees</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center">
              <v-avatar color="success" class="mr-4">
                <v-icon>mdi-check-circle</v-icon>
              </v-avatar>
              <div>
                <div class="text-h4 font-weight-bold">{{ activeCommittees }}</div>
                <div class="text-caption text-medium-emphasis">Active Committees</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center">
              <v-avatar color="info" class="mr-4">
                <v-icon>mdi-account-multiple</v-icon>
              </v-avatar>
              <div>
                <div class="text-h4 font-weight-bold">{{ totalMembers }}</div>
                <div class="text-caption text-medium-emphasis">Total Members</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="d-flex align-center">
              <v-avatar color="warning" class="mr-4">
                <v-icon>mdi-star</v-icon>
              </v-avatar>
              <div>
                <div class="text-h4 font-weight-bold">{{ committeesWithChairs }}</div>
                <div class="text-caption text-medium-emphasis">With Chairs</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Committees List -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center pa-4">
            <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
            <span>Committees</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search committees"
              single-line
              hide-details
              density="compact"
              variant="outlined"
              class="mr-4"
              style="max-width: 300px;"
            ></v-text-field>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="filteredCommittees"
            :loading="loading"
            :search="search"
            class="elevation-0"
          >
            <!-- Name Column -->
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar color="primary" size="40" class="mr-3">
                  <v-icon color="white">mdi-account-group</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="text-caption text-medium-emphasis">{{ item.description }}</div>
                </div>
              </div>
            </template>

            <!-- Chair Column -->
            <template v-slot:item.chair="{ item }">
              <div v-if="item.chair">
                <div class="font-weight-medium">{{ item.chair.full_name }}</div>
                <div class="text-caption text-medium-emphasis">{{ item.chair.email }}</div>
              </div>
              <v-chip v-else size="small" color="warning" variant="outlined">
                No Chair Assigned
              </v-chip>
            </template>

            <!-- Members Column -->
            <template v-slot:item.members_count="{ item }">
              <v-chip color="info" size="small">
                <v-icon start size="small">mdi-account-multiple</v-icon>
                {{ item.members_count }} Members
              </v-chip>
            </template>

            <!-- Status Column -->
            <template v-slot:item.is_active="{ item }">
              <v-chip
                :color="item.is_active ? 'success' : 'error'"
                size="small"
                variant="flat"
              >
                {{ item.is_active ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <!-- Actions Column -->
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon="mdi-eye"
                size="small"
                variant="text"
                @click.stop="viewCommittee(item)"
                class="mr-1"
              ></v-btn>
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click.stop="editCommittee(item)"
                class="mr-1"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click.stop="confirmDelete(item)"
              ></v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 pa-6 bg-primary">
          <v-icon class="mr-2">{{ editMode ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ editMode ? 'Edit Committee' : 'Create Committee' }}
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="formRef" v-model="formValid">
            <v-text-field
              v-model="form.name"
              label="Committee Name"
              :rules="[rules.required]"
              variant="outlined"
              prepend-inner-icon="mdi-account-group"
              class="mb-4"
            ></v-text-field>

            <v-textarea
              v-model="form.description"
              label="Description"
              variant="outlined"
              prepend-inner-icon="mdi-text"
              rows="3"
              class="mb-4"
            ></v-textarea>

            <v-select
              v-model="form.chair_id"
              :items="boardMembers"
              item-title="full_name"
              item-value="user_id"
              label="Committee Chair (Optional)"
              variant="outlined"
              prepend-inner-icon="mdi-star"
              clearable
              class="mb-4"
            ></v-select>

            <v-switch
              v-model="form.is_active"
              label="Active"
              color="primary"
              hide-details
            ></v-switch>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid"
            @click="saveCommittee"
          >
            {{ editMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5 pa-6">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>

        <v-card-text class="pa-6">
          Are you sure you want to delete the committee "<strong>{{ selectedCommittee?.name }}</strong>"?
          This action cannot be undone.
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="deleteCommittee"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useToast } from 'vue-toastification'

const router = useRouter()
const toast = useToast()

// Data
const committees = ref([])
const boardMembers = ref([])
const loading = ref(false)
const search = ref('')
const dialog = ref(false)
const deleteDialog = ref(false)
const editMode = ref(false)
const formValid = ref(false)
const saving = ref(false)
const deleting = ref(false)
const formRef = ref(null)
const selectedCommittee = ref(null)

const form = ref({
  name: '',
  description: '',
  chair_id: null,
  is_active: true
})

// Table headers
const headers = [
  { title: 'Committee', key: 'name', sortable: true },
  { title: 'Chair', key: 'chair', sortable: false },
  { title: 'Members', key: 'members_count', sortable: true },
  { title: 'Status', key: 'is_active', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
]

// Validation rules
const rules = {
  required: value => !!value || 'This field is required'
}

// Computed
const filteredCommittees = computed(() => {
  if (!search.value) return committees.value
  const searchLower = search.value.toLowerCase()
  return committees.value.filter(c =>
    c.name.toLowerCase().includes(searchLower) ||
    c.description?.toLowerCase().includes(searchLower)
  )
})

const activeCommittees = computed(() => {
  return committees.value.filter(c => c.is_active).length
})

const totalMembers = computed(() => {
  return committees.value.reduce((sum, c) => sum + (c.members_count || 0), 0)
})

const committeesWithChairs = computed(() => {
  return committees.value.filter(c => c.chair_id).length
})

// Methods
const fetchCommittees = async () => {
  try {
    loading.value = true
    const response = await api.get('/committees')
    committees.value = response.data.committees || []
  } catch (error) {
    console.error('Error fetching committees:', error)
    toast.error('Failed to load committees')
  } finally {
    loading.value = false
  }
}

const fetchBoardMembers = async () => {
  try {
    const response = await api.get('/members')
    boardMembers.value = response.data.members || []
  } catch (error) {
    console.error('Error fetching board members:', error)
  }
}

const openCreateDialog = () => {
  editMode.value = false
  form.value = {
    name: '',
    description: '',
    chair_id: null,
    is_active: true
  }
  dialog.value = true
}

const editCommittee = (committee) => {
  editMode.value = true
  selectedCommittee.value = committee
  form.value = {
    name: committee.name,
    description: committee.description || '',
    chair_id: committee.chair_id,
    is_active: committee.is_active
  }
  dialog.value = true
}

const closeDialog = () => {
  dialog.value = false
  editMode.value = false
  selectedCommittee.value = null
  form.value = {
    name: '',
    description: '',
    chair_id: null,
    is_active: true
  }
  formRef.value?.reset()
}

const saveCommittee = async () => {
  if (!formValid.value) return

  try {
    saving.value = true
    if (editMode.value) {
      await api.put(`/committees/${selectedCommittee.value.id}`, form.value)
      toast.success('Committee updated successfully')
    } else {
      await api.post('/committees', form.value)
      toast.success('Committee created successfully')
    }
    closeDialog()
    await fetchCommittees()
  } catch (error) {
    console.error('Error saving committee:', error)
    toast.error(error.response?.data?.message || 'Failed to save committee')
  } finally {
    saving.value = false
  }
}

const confirmDelete = (committee) => {
  selectedCommittee.value = committee
  deleteDialog.value = true
}

const deleteCommittee = async () => {
  try {
    deleting.value = true
    await api.delete(`/committees/${selectedCommittee.value.id}`)
    toast.success('Committee deleted successfully')
    deleteDialog.value = false
    await fetchCommittees()
  } catch (error) {
    console.error('Error deleting committee:', error)
    toast.error(error.response?.data?.message || 'Failed to delete committee')
  } finally {
    deleting.value = false
  }
}

const viewCommittee = (committee) => {
  router.push(`/committees/${committee.id}`)
}

// Lifecycle
onMounted(() => {
  fetchCommittees()
  fetchBoardMembers()
})
</script>

<style scoped>
.v-avatar {
  flex-shrink: 0;
}
</style>
